import React, { useState, useEffect } from 'react';
import './AdminUsersListing.css';
import Dashboard from './Dashboard.js';
import { useNavigate } from 'react-router';
import { useCompany } from '../context/CompanyContext.js';

const AdminUsersListing = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  
  // Enhanced date filter state
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: '',
    display: 'Jul 19 - Jul 25'
  });
  const [showDatePicker, setShowDatePicker] = useState(false);
  
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState(''); 
  const [filters, setFilters] = useState({
    status: [],
    userType: [],
    search: '',
    dateFrom: '',
    dateTo: ''
  });
  
  const navigate = useNavigate();

  const userTypeOptions = {
    'platform_company': 'Company',
    'platform_user': 'User',
    'admin': 'Admin'
  };
  
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('Authentication token not found');
        setLoading(false);
        return;
      }

      const params = new URLSearchParams({
        page: currentPage.toString(),
        size: pageSize.toString(),
      });

      // Add filters to params
      if (filters.status.length > 0) {
        params.append('status', filters.status.join(','));
      }
      if (filters.userType.length > 0) {
        params.append('user_type', filters.userType.join(','));
      }
      if (filters.search.trim()) {
        params.append('query', filters.search.trim());
      }
      if (filters.dateFrom) {
        params.append('date_from', filters.dateFrom);
      }
      if (filters.dateTo) {
        params.append('date_to', filters.dateTo);
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin_analytics/list-users?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
        setTotalUsers(data.total || 0);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to fetch users');
      }
    } catch (error) {
      setError('Failed to fetch users');
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [currentPage, pageSize, filters]);

  const handleFilterChange = (filterType, value, checked) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      
      if (filterType === 'search') {
        newFilters.search = value;
      } else {
        if (checked) {
          newFilters[filterType] = [...prev[filterType], value];
        } else {
          newFilters[filterType] = prev[filterType].filter(item => item !== value);
        }
      }
      
      return newFilters;
    });
    
    setCurrentPage(1);
  };

  // Date filter handlers
  const handleDateRangeChange = (startDate, endDate) => {
    setFilters(prev => ({
      ...prev,
      dateFrom: startDate,
      dateTo: endDate
    }));
    
    // Update display
    if (startDate && endDate) {
      const start = new Date(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const end = new Date(endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      setDateRange({
        startDate,
        endDate,
        display: `${start} - ${end}`
      });
    }
    
    setCurrentPage(1);
    setShowDatePicker(false);
  };

  // Predefined date ranges
  const applyDateRange = (range) => {
    const today = new Date();
    let startDate, endDate;
    
    switch (range) {
      case 'today':
        startDate = endDate = today.toISOString().split('T')[0];
        break;
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        startDate = endDate = yesterday.toISOString().split('T')[0];
        break;
      case 'last7days':
        endDate = today.toISOString().split('T')[0];
        startDate = new Date(today.setDate(today.getDate() - 7)).toISOString().split('T')[0];
        break;
      case 'last30days':
        endDate = new Date().toISOString().split('T')[0];
        startDate = new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0];
        break;
      case 'thisMonth':
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        startDate = firstDay.toISOString().split('T')[0];
        endDate = lastDay.toISOString().split('T')[0];
        break;
      default:
        return;
    }
    
    handleDateRangeChange(startDate, endDate);
  };

  const clearAllFilters = () => {
    setFilters({
      status: [],
      userType: [],
      search: '',
      dateFrom: '',
      dateTo: ''
    });
    setSearchTerm('');
    setDateRange({
      startDate: '',
      endDate: '',
      display: 'Select Date Range'
    });
    setCurrentPage(1);
  };

  const getActiveFilterCount = () => {
    return filters.status.length + 
           filters.userType.length + 
           (filters.search ? 1 : 0) + 
           (filters.dateFrom && filters.dateTo ? 1 : 0);
  };

  // Enhanced badge functions
  const getUserTypeBadgeClass = (userType) => {
    switch (userType.toLowerCase()) {
      case 'platform_user':
        return 'admin-users-user-type admin-users-user-type--user';
      case 'platform_company':
        return 'admin-users-user-type admin-users-user-type--company';
      case 'admin':
        return 'admin-users-user-type admin-users-user-type--admin';
      default:
        return 'admin-users-user-type admin-users-user-type--default';
    }
  };

  const getUserStatusBadgeClass = (isActive) => {
    return isActive 
      ? 'admin-users-status-badge admin-users-status--active' 
      : 'admin-users-status-badge admin-users-status--inactive';
  };

  const getUserTypetTag = (userType) => {
    switch (userType.toLowerCase()) {
      case 'platform_user':
        return 'Platform User';
      case 'platform_company':
        return 'Platform Company';
      case 'admin':
        return 'Admin';
      default:
        return 'Unknown';
    }
  };

  // Time since last online
  const getTimeSinceOnline = (dateString) => {
    const lastOnline = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - lastOnline);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays} days ago`;
    if (diffDays <= 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    return `${Math.ceil(diffDays / 30)} months ago`;
  };

  // User activity indicator
  const getUserActivityStatus = (lastOnlineDate, isActive) => {
    if (!isActive) return { status: 'inactive', text: 'Inactive' };
    
    const lastOnline = new Date(lastOnlineDate);
    const now = new Date();
    const diffTime = Math.abs(now - lastOnline);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 1) return { status: 'online', text: 'Recently Active' };
    if (diffDays <= 7) return { status: 'recent', text: 'Active This Week' };
    if (diffDays <= 30) return { status: 'moderate', text: 'Active This Month' };
    return { status: 'dormant', text: 'Dormant' };
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const totalPages = Math.ceil(totalUsers / pageSize);

  return (
    <Dashboard page={"users"}>
      <div className="admin-users-listing-container">
        <div className="admin-users-listing-content">
          <div className="admin-users-content-header">
            <div className="admin-users-header-info">
              <h1>Users Listing</h1>
              <p className="admin-users-date-range-text">
                Showing {users.length} of {totalUsers} users
                {getActiveFilterCount() > 0 && ` (${getActiveFilterCount()} filter${getActiveFilterCount() > 1 ? 's' : ''} applied)`}
              </p>
            </div>
            <div className="admin-users-date-range-selector">
              <button 
                className="admin-users-date-range-btn"
                onClick={() => setShowDatePicker(!showDatePicker)}
              >
                {dateRange.display} 📅
              </button>
              
              {showDatePicker && (
                <div className="date-picker-dropdown">
                  <div className="date-picker-header">
                    <h4>Select Date Range</h4>
                    <button 
                      onClick={() => setShowDatePicker(false)}
                      className="close-picker-btn"
                    >
                      ×
                    </button>
                  </div>
                  
                  <div className="date-picker-presets">
                    <button onClick={() => applyDateRange('today')}>Today</button>
                    <button onClick={() => applyDateRange('yesterday')}>Yesterday</button>
                    <button onClick={() => applyDateRange('last7days')}>Last 7 Days</button>
                    <button onClick={() => applyDateRange('last30days')}>Last 30 Days</button>
                    <button onClick={() => applyDateRange('thisMonth')}>This Month</button>
                  </div>
                  
                  <div className="date-picker-custom">
                    <h5>Custom Range</h5>
                    <div className="date-inputs">
                      <input
                        type="date"
                        value={dateRange.startDate}
                        onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                        className="date-input"
                      />
                      <span>to</span>
                      <input
                        type="date"
                        value={dateRange.endDate}
                        onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                        className="date-input"
                      />
                    </div>
                    <button 
                      onClick={() => handleDateRangeChange(dateRange.startDate, dateRange.endDate)}
                      disabled={!dateRange.startDate || !dateRange.endDate}
                      className="apply-date-btn"
                    >
                      Apply Range
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="admin-users-list-section">
            <div className="admin-users-section-header">
              <div className="admin-users-header-actions">
                <div className="admin-users-search-container">
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      handleFilterChange('search', e.target.value);
                    }}
                    className="admin-users-search-input"
                  />
                  <span className="admin-users-search-icon">🔍</span>
                </div>
                <button 
                  className="admin-users-filters-btn"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <span className="admin-users-filter-icon">⚙️</span>
                  Filters
                  {getActiveFilterCount() > 0 && (
                    <span className="admin-users-filter-count">{getActiveFilterCount()}</span>
                  )}
                </button>
              </div>
            </div>

            {showFilters && (
              <div className="admin-users-filters-panel">
                <div className="admin-users-filters-content">
                  <div className="admin-users-filter-group">
                    <h4>User Status</h4>
                    <div className="admin-users-filter-options">
                      {['active', 'inactive'].map(status => (
                        <label key={status} className="admin-users-filter-option">
                          <input
                            type="checkbox"
                            checked={filters.status.includes(status)}
                            onChange={(e) => handleFilterChange('status', status, e.target.checked)}
                          />
                          <span className="admin-users-checkmark"></span>
                          <span className="admin-users-option-label">{status.charAt(0).toUpperCase() + status.slice(1)}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="admin-users-filter-group">
                    <h4>User Type</h4>
                    <div className="admin-users-filter-options">
                      {Object.entries(userTypeOptions).map(([key, label]) => (
                        <label key={key} className="admin-users-filter-option">
                          <input
                            type="checkbox"
                            checked={filters.userType.includes(key)}
                            onChange={(e) => handleFilterChange('userType', key, e.target.checked)}
                          />
                          <span className="admin-users-checkmark"></span>
                          <span className="admin-users-option-label">{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="admin-users-filter-actions">
                    <button onClick={clearAllFilters} className="admin-users-clear-filters-btn">
                      Clear All
                    </button>
                    <button onClick={() => setShowFilters(false)} className="admin-users-apply-filters-btn">
                      Apply Filters
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Active Filters Display */}
            {getActiveFilterCount() > 0 && (
              <div className="active-filters-display">
                <div className="active-filters-header">
                  <span>Active Filters:</span>
                  <button onClick={clearAllFilters} className="clear-all-quick-btn">Clear All</button>
                </div>
                <div className="active-filters-list">
                  {filters.status.map(status => (
                    <span key={status} className="filter-chip">
                      Status: {status.charAt(0).toUpperCase() + status.slice(1)}
                      <button 
                        onClick={() => handleFilterChange('status', status, false)}
                        className="filter-chip-remove"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  {filters.userType.map(type => (
                    <span key={type} className="filter-chip">
                      Type: {userTypeOptions[type]}
                      <button 
                        onClick={() => handleFilterChange('userType', type, false)}
                        className="filter-chip-remove"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  {filters.search && (
                    <span className="filter-chip">
                      Search: "{filters.search}"
                      <button 
                        onClick={() => {
                          setSearchTerm('');
                          handleFilterChange('search', '');
                        }}
                        className="filter-chip-remove"
                      >
                        ×
                      </button>
                    </span>
                  )}
                  {filters.dateFrom && filters.dateTo && (
                    <span className="filter-chip">
                      Date: {dateRange.display}
                      <button 
                        onClick={() => {
                          setFilters(prev => ({ ...prev, dateFrom: '', dateTo: '' }));
                          setDateRange({ startDate: '', endDate: '', display: 'Select Date Range' });
                        }}
                        className="filter-chip-remove"
                      >
                        ×
                      </button>
                    </span>
                  )}
                </div>
              </div>
            )}

            <div className="admin-users-table">
              <div className="table-header">
                <div className="header-cell">Email</div>
                <div className="header-cell">Names</div>
                <div className="header-cell">User Type</div>
                <div className="header-cell">Status</div>
                <div className="header-cell">Last Online</div>
                <div className="header-cell">Created</div>
              </div>

              <div className="table-body">
                {loading ? (
                  <div className="admin-users-loading-state">Loading users...</div>
                ) : users.length > 0 ? (
                  users.map((user) => {
                    const activityStatus = getUserActivityStatus(user.last_time_online, user.is_active);
                    return (
                      <div key={user.user_id} className="table-row" onClick={() => navigate(`/view-user-profile/${user.id}`)}>
                        <div className="table-cell email-cell">
                          <div className="user-email-info">
                            <span className="user-email">{user.email}</span>
                            <span className="user-id">ID: {user.id}</span>
                          </div>
                        </div>
                        <div className="table-cell name-cell">
                          <div className="user-name-info">
                            <span className="user-name">{user.first_name} {user.last_name}</span>
                            <span className="user-activity-status admin-users-activity--{activityStatus.status}">
                              {activityStatus.text}
                            </span>
                          </div>
                        </div>
                        <div className="table-cell type-cell">
                          <span className={getUserTypeBadgeClass(user.user_type)}>
                            {getUserTypetTag(user.user_type)}
                          </span>
                        </div>
                        <div className="table-cell status-cell">
                          <span className={getUserStatusBadgeClass(user.is_active)}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <div className="table-cell date-cell">
                          <div className="date-info">
                            <span className="formatted-date">{formatDate(user.last_time_online)}</span>
                            <span className="relative-date">{getTimeSinceOnline(user.last_time_online)}</span>
                          </div>
                        </div>
                        <div className="table-cell date-cell">
                          <span className="formatted-date">{formatDate(user.created_at)}</span>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="admin-users-empty-state">
                    No users found matching your filters.
                    <button onClick={clearAllFilters} className="clear-filters-suggestion">
                      Clear filters to see all users
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="admin-users-pagination">
              <div className="admin-users-pagination-info">
                <span>View</span>
                <select 
                  value={pageSize} 
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="admin-users-page-size-select"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <span>Users per page</span>
              </div>
              
              <div className="admin-users-pagination-controls">
                <button 
                  className="admin-users-page-btn"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  ‹
                </button>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    className={`admin-users-page-btn ${currentPage === page ? 'active' : ''}`}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                ))}
                
                <button 
                  className="admin-users-page-btn"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  ›
                </button>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="admin-users-error-message">
            {error}
          </div>
        )}
      </div>
    </Dashboard>
  );
};

export default AdminUsersListing;